import { CircleAlert, CircleCheck } from "lucide-react";
import { useSearchParams } from "react-router";
import { StatusChip } from "../components/kit";
import { Alert } from "../components/ui";
import {
  Filter,
  IMPORT_LABEL,
  Kpi,
  Loadable,
  PageHead,
  RestaurantLink,
  type Row,
  Section,
  Table,
  text,
  usd,
  useAdmin,
  when,
} from "./ui";

// Operare: imports, AI costs and the system health page (what can break on its own).

const IMPORT_FILTERS: [string, string][] = [["", "Toate"], ...Object.entries(IMPORT_LABEL)];

export function Imports() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const list = useAdmin<{ imports: Row[] }>(["imports", status], `/imports?status=${status}`);
  return (
    <>
      <PageHead
        title="Importuri"
        sub="Fiecare meniu încărcat de restaurante și cât a durat pregătirea lui."
      />
      <Filter
        label="Stare"
        value={status}
        onChange={(v) => setParams(v ? { status: v } : {}, { replace: true })}
        options={IMPORT_FILTERS}
      />
      <Loadable q={list}>
        {(d) => (
          <Table
            head={["Creat", "Restaurant", "Stare", "Pagini", "Eșuate", "Erori", "Pregătire în browser"]}
            rows={d.imports.map((j) => [
              when(j.created_at),
              <RestaurantLink key="r" id={j.restaurant_id} name={j.restaurant} />,
              IMPORT_LABEL[String(j.status)] ?? text(j.status),
              text(j.files),
              text(j.failed_files),
              text(j.file_errors ?? j.error_code),
              j.prep_ms ? `${(Number(j.prep_ms) / 1000).toFixed(1).replace(".", ",")} s` : "—",
            ])}
          />
        )}
      </Loadable>
    </>
  );
}

export function Ai() {
  const overview = useAdmin<{ ai: Row | null; restaurantCapMicro: number | null }>(["overview"], "/overview");
  const usage = useAdmin<{ days: Row[]; recent: Row[] }>(["ai"], "/ai");
  const ai = (overview.data?.ai ?? {}) as Row;
  return (
    <>
      <PageHead
        title="AI și costuri"
        sub="Importurile, traducerile și valorile nutriționale folosesc AI. Costurile sunt estimate din consumul raportat; factura furnizorului se verifică separat."
      />
      <Alert tone="info">
        Singura limită de cheltuială: {usd(overview.data?.restaurantCapMicro ?? 2_000_000, 0)} pe restaurant,
        pentru totdeauna (migrarea 0032). Contorul unui restaurant se repornește doar din fișa lui.
      </Alert>
      <div className="kpi-grid">
        <Kpi label="Furnizor" value={text(ai.provider)} />
        <Kpi label="Cheltuit în total (estimat)" value={usd(ai.spentMicro)} />
        <Kpi label="Rezervat acum" value={usd(ai.reservedMicro, 4)} />
        <Kpi
          label="Oprire de urgență"
          value={ai.killedAt ? "PORNITĂ" : "nu"}
          hint={ai.killedAt ? text(ai.killedReason) : undefined}
          tone={ai.killedAt ? "error" : undefined}
        />
      </div>
      <Loadable q={usage}>
        {(d) => (
          <>
            <Section title="Pe zile (30 de zile)">
              <Table
                head={["Zi", "Apeluri", "Cost estimat", "Tokeni intrare", "Tokeni ieșire"]}
                rows={d.days.map((r) => [
                  text(r.day),
                  text(r.requests),
                  usd(r.spent_micro, 4),
                  Number(r.input_tokens).toLocaleString("ro-RO"),
                  Number(r.output_tokens).toLocaleString("ro-RO"),
                ])}
              />
            </Section>
            <Section title="Ultimele apeluri">
              <Table
                head={["Când", "Scop", "Restaurant", "Model", "Stare", "Cost", "Eroare"]}
                rows={d.recent.map((r) => [
                  when(r.created_at),
                  text(r.purpose),
                  r.restaurant_id && !String(r.restaurant_id).includes(":") ? (
                    <RestaurantLink key="r" id={r.restaurant_id} name="deschide" />
                  ) : (
                    text(r.restaurant_id)
                  ),
                  text(
                    String(r.model ?? "")
                      .split("/")
                      .pop(),
                  ),
                  text(r.status),
                  usd(r.actual_micro ?? r.reserved_micro, 4),
                  text(r.error),
                ])}
              />
            </Section>
          </>
        )}
      </Loadable>
    </>
  );
}

interface Health {
  environment: string;
  providers: Record<string, string | boolean>;
  email: { month: string; sent: number; cap: number };
  ai: Row | null;
  stuckImports: Row[];
  runningRuns: Row[];
  stuckAi: Row[];
  failedFiscal: Row[];
  billingErrors: Row[];
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return ok ? (
    <StatusChip tone="success" icon={CircleCheck} label={label} />
  ) : (
    <StatusChip tone="error" icon={CircleAlert} label={label} />
  );
}

export function System() {
  const q = useAdmin<Health>(["system"], "/system");
  return (
    <>
      <PageHead
        title="Sănătate sistem"
        sub="Ce se poate bloca singur: importuri, apeluri AI, facturi fiscale, e-mail."
      />
      <Loadable q={q}>
        {(d) => {
          const p = d.providers;
          const problems =
            d.stuckImports.length + d.stuckAi.length + d.failedFiscal.length + (d.ai?.killed_at ? 1 : 0);
          return (
            <>
              {problems === 0 ? (
                <Alert tone="success">Totul funcționează: nimic blocat, nicio factură refuzată.</Alert>
              ) : (
                <Alert tone="warning" title={`${problems} probleme de verificat mai jos`} />
              )}
              <Section title="Servicii">
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <Check ok={p.ai !== "none"} label={`AI: ${String(p.ai)}`} />
                  <Check ok={p.email !== "none"} label={`E-mail: ${String(p.email)}`} />
                  <Check ok={p.billing === "stripe"} label={`Plăți: ${String(p.billing)}`} />
                  <Check ok={!!p.smartbill} label="SmartBill" />
                  <Check ok={!!p.turnstile} label="Turnstile (anti-roboți)" />
                  <Check
                    ok={p.registration === "open"}
                    label={`Înregistrare: ${p.registration === "open" ? "deschisă" : "invitație"}`}
                  />
                  <Check
                    ok={!d.ai?.killed_at}
                    label={d.ai?.killed_at ? "AI oprit de urgență" : "AI fără oprire de urgență"}
                  />
                </div>
                <div className="kpi-grid">
                  <Kpi
                    label={`E-mailuri trimise (${d.email.month})`}
                    value={`${d.email.sent} / ${d.email.cap}`}
                    tone={d.email.sent > d.email.cap * 0.8 ? "warning" : undefined}
                  />
                  <Kpi
                    label="Mediu"
                    value={d.environment === "staging" ? "producție (staging)" : d.environment}
                  />
                </div>
              </Section>
              <Section title="Importuri blocate (peste 30 de minute)">
                <Table
                  head={["Creat", "Restaurant", "Stare"]}
                  empty="Niciunul."
                  rows={d.stuckImports.map((j) => [
                    when(j.created_at),
                    <RestaurantLink key="r" id={j.restaurant_id} name={j.restaurant} />,
                    IMPORT_LABEL[String(j.status)] ?? text(j.status),
                  ])}
                />
              </Section>
              <Section title="Analize nutriționale în curs">
                <Table
                  head={["Pornită", "Restaurant", "Progres"]}
                  empty="Niciuna."
                  rows={d.runningRuns.map((n) => [
                    when(n.started_at),
                    <RestaurantLink key="r" id={n.restaurant_id} name={n.restaurant} />,
                    `${text(n.products_done)} / ${text(n.products_total)}`,
                  ])}
                />
              </Section>
              <Section title="Apeluri AI rămase deschise (peste 15 minute)">
                <Table
                  head={["Creat", "Scop", "Model", "Stare"]}
                  empty="Niciunul. Cronul de noapte le închide pe cele rămase."
                  rows={d.stuckAi.map((a) => [
                    when(a.created_at),
                    text(a.purpose),
                    text(a.model),
                    text(a.status),
                  ])}
                />
              </Section>
              <Section title="Facturi fiscale refuzate">
                <Table
                  head={["Plătită", "Restaurant", "Motiv", "Încercări"]}
                  empty="Niciuna."
                  rows={d.failedFiscal.map((i) => [
                    when(i.paid_at),
                    <RestaurantLink key="r" id={i.restaurant_id} name={i.restaurant} />,
                    text(i.fiscal_error),
                    text(i.fiscal_attempts),
                  ])}
                />
              </Section>
              {d.billingErrors.length > 0 && (
                <Section title="Evenimente de plată cu eroare">
                  <Table
                    head={["Primit", "Tip", "Rezultat"]}
                    rows={d.billingErrors.map((e) => [when(e.received_at), text(e.type), text(e.outcome)])}
                  />
                </Section>
              )}
            </>
          );
        }}
      </Loadable>
    </>
  );
}
